/**
 * queryKeys.ts — query-key factories for the admin TanStack Query cache.
 *
 * Why factories: keeping every key under a single namespaced builder lets
 * `queryClient.invalidateQueries({ queryKey: qk.dispatcher.all })` invalidate
 * an entire domain without enumerating every individual key, while leaf
 * keys stay precise enough for parameterised queries.
 *
 * Each domain (rides, fleet, users, …) extends this file with its
 * own sub-namespace; the socket→invalidate bridge in
 * `hooks/useSocketQueryBridge.ts` maps server events to these prefixes.
 */

/** Top-level namespaces. New domains slot in here. */
export const qk = {
  dispatcher: {
    /** Invalidates every dispatcher-scoped query. */
    all:    ['dispatcher'] as const,
    /** GET /api/dispatcher/active — live on-shift fleet snapshot. */
    active: ['dispatcher', 'active'] as const,
  },
  rides: {
    /** Invalidates every ride-scoped query. */
    all:    ['rides'] as const,
    /** GET /api/rides with params — parameterised list (Orders, Dispatcher map, Scheduler). */
    list:   (params: Record<string, unknown> = {}) =>
      ['rides', 'list', params] as const,
    /** GET /api/rides/today — Scheduler OrdersStrip. */
    today:  ['rides', 'today'] as const,
    /** GET /api/reports/rides with params — Reports table. */
    reports: (params: Record<string, unknown> = {}) =>
      ['rides', 'reports', params] as const,
    /** GET /api/reports/revenue with params — dashboard revenue tile. */
    revenue: (params: Record<string, unknown> = {}) =>
      ['rides', 'revenue', params] as const,
    /** GET /api/reports/summary with params — Reports analytics board. */
    summary: (params: Record<string, unknown> = {}) =>
      ['rides', 'summary', params] as const,
    /** GET /api/rides/:id/history — Reports row expansion. */
    history: (rideId: string) =>
      ['rides', 'history', rideId] as const,
  },
  fleet: {
    /** Invalidates every fleet-scoped query — vehicles + drivers + features + types. */
    all:           ['fleet'] as const,
    /** GET /api/fleet/vehicles — admin fleet table + Orders/Scheduler filters. */
    vehicles:      (params: Record<string, unknown> = {}) =>
      ['fleet', 'vehicles', params] as const,
    /** GET /api/fleet/drivers — driver dropdowns + Reports filter. */
    drivers:       ['fleet', 'drivers'] as const,
    /** GET /api/fleet/features — vehicle feature catalog. */
    features:      ['fleet', 'features'] as const,
    /** GET /api/vehicle-types — vehicle type catalog. */
    vehicleTypes:  ['fleet', 'vehicle-types'] as const,
  },
  /** Machine credentials for external integrations (admin-only surface). */
  serviceAccounts: {
    /** Invalidates every service-account query. */
    all:  ['service-accounts'] as const,
    /** GET /api/service-accounts — admin list. */
    list: ['service-accounts', 'list'] as const,
  },

  users: {
    /** Invalidates every user/group/org-scoped query. */
    all:           ['users'] as const,
    /** GET /api/users — admin user list (with optional ?role filter etc.). */
    list:          (params: Record<string, unknown> = {}) =>
      ['users', 'list', params] as const,
    /** GET /api/users/:id — user detail (admin dialogs). */
    detail:        (userId: string) => ['users', 'detail', userId] as const,
    /** GET /api/users/:id/equipment — user equipment list. */
    equipment:     (userId: string) => ['users', 'equipment', userId] as const,
    /** GET /api/groups — admin group list. */
    groups:        ['users', 'groups'] as const,
    /** GET /api/groups/:id/members — members of one group. */
    groupMembers:  (groupId: string) => ['users', 'group-members', groupId] as const,
    /** GET /api/organizations — admin org table. */
    organizations: ['users', 'organizations'] as const,
    /** GET /api/organizations/:id/members — members of one org. */
    orgMembers:    (orgId: string) => ['users', 'org-members', orgId] as const,
    /** GET /api/organizations/tree — hierarchical tree for the picker. */
    orgTree:       ['users', 'org-tree'] as const,
    /** GET /api/user-equipment — admin catalog of equipment options. */
    userEquipment: ['users', 'user-equipment'] as const,
  },
  recurringTrips: {
    /** Invalidates every recurring-trip-scoped query. */
    all:    ['recurringTrips'] as const,
    /** GET /api/recurring-trips with params — the recurring-trips list page. */
    list:   (params: Record<string, unknown> = {}) =>
      ['recurringTrips', 'list', params] as const,
    /** GET /api/recurring-trips/:id — single-template detail. */
    detail: (id: string) => ['recurringTrips', 'detail', id] as const,
  },
  institutions: {
    /** Invalidates every institution-scoped query. */
    all:  ['institutions'] as const,
    /** GET /api/institutions — admin list + autocomplete source. */
    list: (params: Record<string, unknown> = {}) =>
      ['institutions', 'list', params] as const,
  },
  addresses: {
    /** Invalidates every address-search query. */
    all:    ['addresses'] as const,
    /** GET /api/addresses/search — local OSM autocomplete. */
    search: (params: Record<string, unknown> = {}) =>
      ['addresses', 'search', params] as const,
    /** /api/tomtom-proxy/search — TomTom fallback path. */
    tomtom: (params: Record<string, unknown> = {}) =>
      ['addresses', 'tomtom', params] as const,
  },
  audit: {
    /** Invalidates every audit-log-scoped query. */
    all:  ['audit'] as const,
    /** GET /api/audit with filters — admin audit log. */
    list: (params: Record<string, unknown> = {}) =>
      ['audit', 'list', params] as const,
  },
  devices: {
    /** Invalidates every device + activation-code query. */
    all:             ['devices'] as const,
    /** GET /api/activation-codes — admin codes list. */
    activationCodes: (params: Record<string, unknown> = {}) =>
      ['devices', 'activation-codes', params] as const,
    /** GET /api/devices — active driver devices. */
    active:          ['devices', 'active'] as const,
  },
  documents: {
    /** Invalidates every document query — owner lists, per-owner lists, types. */
    all:    ['documents'] as const,
    /** GET /api/documents/owners — one page of a Documents-page tab. */
    owners: (params: Record<string, unknown> = {}) =>
      ['documents', 'owners', params] as const,
    /** GET /api/documents — documents attached to one owner. */
    list:   (entity: string, entityId: string) =>
      ['documents', 'list', entity, entityId] as const,
    /** GET /api/document-types — upload dropdown catalog for one entity. */
    types:  (entity: string) => ['documents', 'types', entity] as const,
  },
  resources: {
    /** Invalidates every resource-file query. */
    all:  ['resources'] as const,
    /** GET /api/resources — downloadable file list for the Resources page. */
    list: ['resources', 'list'] as const,
  },
  config: {
    /** Invalidates every config query. */
    all:         ['config'] as const,
    /** GET /api/admin/mapbox-token — runtime-delivered Mapbox `pk.*` token. */
    mapboxToken: ['config', 'mapbox-token'] as const,
  },
} as const;
